import { salesHighlightsText, visitHighlightsText } from '@features/campaigns';
import {
  Lci3Report,
  Lci3SalesMetrics,
  VisitMetrics,
} from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { formatters } from '@utils/formattersV2';
import { CustomFields } from '@features/lci-report/components/VisitationLciReportSetupForm';
import {
  addNewlinesToSentences,
  wrapBoldValue,
  wrapLciValue,
} from '@features/lci-report/utils/default-text';

export const getFtaSummaryDefaultText = (
  selectedLciStat: Lci3Report['visitMetrics'],
  campaignName: CustomFields['campaignName']
) => {
  const textList = visitHighlightsText({
    metrics: {
      uniqueUsers: selectedLciStat.uniqueUsers ?? 0,
      projectedVisits: selectedLciStat.projectedVisits,
      incrementalLift: selectedLciStat.incrementalLift,
      incrementalVisits: selectedLciStat.incrementalVisits,
    },
    campaignName,
    formatValues: {
      uniqueUsers: wrapBoldValue,
      projectedVisits: wrapBoldValue,
      incrementalLift: wrapLciValue,
      incrementalVisits: wrapLciValue,
    },
  });

  return addNewlinesToSentences(textList.join(''));
};

export const getRsaSummaryDefaultText = (
  selectedLciStat: VisitMetrics,
  salesAttributionSummaryReport: Lci3SalesMetrics,
  campaignName: CustomFields['campaignName']
) => {
  if (!salesAttributionSummaryReport) return '';

  const textList = salesHighlightsText({
    metrics: {
      uniqueUsers: selectedLciStat.uniqueUsers ?? 0,
      totalProjectedSales:
        salesAttributionSummaryReport.totalProjectedSales.total,
      incrementalSalesLift:
        salesAttributionSummaryReport.incrementalSalesLift.total,
      incrementalSales: salesAttributionSummaryReport.incrementalSales.total,
    },
    campaignName,
    formatValues: {
      uniqueUsers: wrapBoldValue,
      totalProjectedSales: value => `<vr>${value}</vr>`,
      incrementalSalesLift: wrapLciValue,
      incrementalSales: wrapLciValue,
    },
  });

  return addNewlinesToSentences(textList.join(''));
};

export const getFtaKeysDefaultText = (
  selectedLciStat: Lci3Report['visitMetrics'],
  campaignName: CustomFields['campaignName']
) => {
  const projectedVisits = formatters.visits(selectedLciStat.projectedVisits, {
    full: true,
  });

  const incrementalLift = formatters.lift(selectedLciStat.incrementalLift);
  const incrementalVisits = formatters.visits(
    selectedLciStat.incrementalVisits,
    { full: true }
  );
  const uniqueUsers = formatters.users(selectedLciStat.uniqueUsers, {
    full: true,
  });

  return [
    `The ${campaignName} campaign reached <b>${uniqueUsers}</b> users who visited <vr>${projectedVisits}</vr> times after being exposed.`,
    `Compared to audience matched control users, the matched exposed users visited at a rate <lci>${incrementalLift}</lci> higher which is indicated by the Incremental Lift.`,
    `The campaign generated <lci>${incrementalVisits}</lci> Incremental Visits that would not have occurred without exposure to this campaign.`,
  ].join('\n');
};
